import apiClient from "./apiClient";

export interface Scenario {
    id: string;
    title: string;
    description?: string;
    industry: string;
    difficulty: string;
    avgDurationMin: number;
    personaTitle?: string;
    personaStyle?: string;
    meetingType: string;
    keyObjections: string[];
    scriptHints?: string;
    isSystemDefault: boolean;
    isArchived: boolean;
    createdAt: string;
    updatedAt: string;
    CreatedBy?: { id: string; name: string } | null;
}

export interface ScenarioListResponse {
    scenarios: Scenario[];
    total: number;
    page: number;
    limit: number;
}

export interface CreateScenarioPayload {
    title: string;
    description?: string;
    industry: string;
    difficulty?: string;
    avgDurationMin?: number;
    personaTitle?: string;
    personaStyle?: string;
    meetingType?: string;
    keyObjections: string[];
    scriptHints?: string;
}

export interface GenerateAIPayload {
    industry: string;
    meetingType?: string;
    difficulty?: string;
    additionalContext?: string;
    saveImmediately?: boolean;
}

export interface GenerateUrlPayload {
    url: string;
    meetingType?: string;
    difficulty?: string;
    saveImmediately?: boolean;
}

const BASE = "/v1/scenarios";

const scenarioService = {
    getScenarios: async (params?: {
        industry?: string;
        search?: string;
        difficulty?: string;
        dateFilter?: string;
        sortDate?: string;
        page?: number;
        limit?: number;
    }): Promise<ScenarioListResponse> => {
        const res = await apiClient.get(BASE, { params });
        return res.data.data;
    },

    getIndustries: async (): Promise<string[]> => {
        const res = await apiClient.get(`${BASE}/industries`);
        return res.data.data;
    },

    getScenarioById: async (id: string): Promise<Scenario> => {
        const res = await apiClient.get(`${BASE}/${id}`);
        return res.data.data;
    },

    createScenario: async (payload: CreateScenarioPayload): Promise<Scenario> => {
        const res = await apiClient.post(BASE, payload);
        return res.data.data;
    },

    generateWithAI: async (payload: GenerateAIPayload): Promise<Scenario & { isPreview?: boolean }> => {
        const res = await apiClient.post(`${BASE}/generate-ai`, payload);
        return res.data.data;
    },

    generateFromUrl: async (payload: GenerateUrlPayload): Promise<Scenario & { isPreview?: boolean }> => {
        const res = await apiClient.post(`${BASE}/generate-url`, payload);
        return res.data.data;
    },

    updateScenario: async (id: string, payload: Partial<CreateScenarioPayload>): Promise<Scenario> => {
        const res = await apiClient.patch(`${BASE}/${id}`, payload);
        return res.data.data;
    },

    archiveScenario: async (id: string): Promise<void> => {
        await apiClient.delete(`${BASE}/${id}`);
    },

    seedScenarios: async (): Promise<{ seeded: number; message: string }> => {
        const res = await apiClient.post(`${BASE}/seed`);
        return res.data.data;
    },

    assignScenario: async (id: string, payload: { userIds?: string[], teamIds?: string[] }): Promise<any> => {
        const res = await apiClient.post(`${BASE}/${id}/assign`, payload);
        return res.data.data;
    },

    getScenarioAssignments: async (id: string): Promise<{ userIds: string[], teamIds: string[] }> => {
        const res = await apiClient.get(`${BASE}/${id}/assignments`);
        return res.data.data;
    },
};

export default scenarioService;
